/**
 * Bottom-sheet behaviour, ported from the p2p platform's shared `<BottomSheet>` and its
 * currency-picker consumer.
 *
 * p2p implements its sheets with framer-motion: a spring slide-up, a fading backdrop,
 * and drag-to-dismiss. The wallet ships three runtime dependencies and writes native CSS
 * by policy (frontend/CLAUDE.md), so pulling in an animation library would be the wrong
 * trade. The motion lives in CSS; only the gestures, which CSS cannot express, live here.
 *
 * TWO gestures, both p2p's:
 *
 *  1. HANDLE DRAG — pull the grab handle or title. Always available.
 *
 *  2. PULL-TO-CLOSE HANDOFF — the one that makes a sheet feel native. When the scroll
 *     list is already at the top and the user keeps pulling DOWN, the gesture hands off
 *     from the list to the sheet, so the sheet follows the finger from anywhere in the
 *     content. Without it a sheet only dismisses from a thin strip at the top, which is
 *     the difference between "this feels like iOS" and "this feels like a web page".
 *     p2p implements it in MobileAssetBalanceCard's currency picker; the 8px slop and
 *     the abandon-if-scrolled rule below are its numbers.
 *
 * Dismissal is p2p's heuristic exactly, so both products feel the same in the hand: past
 * 80px OR a downward flick faster than 500px/s closes; anything less springs back.
 *
 * Dragging is downward-only — an upward pull must not lift the sheet off its stop and
 * expose the page behind it. Body scroll is locked while a sheet is open so a gesture
 * that misses the sheet cannot scroll the page underneath.
 */

import { useCallback, useEffect, useRef } from 'react';
import type {
  PointerEvent as ReactPointerEvent,
  RefObject,
  TouchEvent as ReactTouchEvent,
  UIEvent as ReactUIEvent,
} from 'react';

/** Past this many pixels of downward travel, releasing dismisses. p2p's threshold. */
const DISMISS_DISTANCE_PX = 80;
/** Or a flick faster than this, in px/s, regardless of distance. p2p's threshold. */
const DISMISS_VELOCITY_PX_S = 500;
/** Downward slop before a pull at the top of the list becomes a dismiss, matching the
 *  browser's own tap-versus-drag threshold so a tap never starts one. p2p uses 8. */
const HANDOFF_SLOP_PX = 8;
/**
 * Fraction of the pull the sheet actually travels. A light resistance keeps the sheet
 * weighted without leaving it visibly behind the user's finger. The dismissal test
 * still uses the raw pointer offset, so this affects motion rather than intent.
 */
const DRAG_ELASTIC = 0.72;
/** Short exit continuation after a successful pull. */
const EXIT_DURATION_MS = 240;
const SETTLE_DURATION_MS = 220;

/**
 * How many sheets currently hold the body-scroll lock.
 *
 * Module scope, not per-instance. Each instance saving and restoring
 * `document.body.style.overflow` looks composable but is not: when a whole subtree is
 * torn down at once — a navigation while a sheet is open inside another — React runs
 * the PARENT's cleanup before the child's, so the outer sheet restores '' and the inner
 * one then writes back 'hidden'. No sheet remains and the page is permanently frozen.
 * A count with a single snapshot cannot get into that state whatever the teardown order.
 */
let lockCount = 0;
let lockedOverflow = '';

/**
 * Freeze the page behind a sheet, reference-counted across every sheet that asks.
 *
 * Module-private again. It was exported for the full-page menu, which needed the lock
 * without the gestures; that menu is gone. The refcount still matters with one caller:
 * two sheets can overlap, and independent locks would each snapshot the other's
 * 'hidden' and write it back on unmount, leaving the page frozen with nothing on screen.
 */
function useBodyScrollLock(): void {
  useEffect(() => {
    if (lockCount === 0) {
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
    return () => {
      lockCount -= 1;
      if (lockCount === 0) document.body.style.overflow = lockedOverflow;
    };
  }, []);
}

export interface BottomSheetHandle {
  /** Spread onto the grab handle and title — the always-available drag origins. */
  dragProps: {
    onPointerDown: (event: ReactPointerEvent) => void;
  };
  /** Spread onto the scroll region to enable the pull-to-close handoff. */
  scrollProps: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onTouchStart: (event: ReactTouchEvent<HTMLElement>) => void;
    onTouchMove: (event: ReactTouchEvent<HTMLElement>) => void;
    onTouchEnd: (event: ReactTouchEvent<HTMLElement>) => void;
    onTouchCancel: () => void;
    onScroll: (event: ReactUIEvent<HTMLElement>) => void;
  };
}

interface BottomSheetOptions {
  /** Allow a downward pull from content even when it is too short to scroll. */
  allowStaticContentPull?: boolean;
}

/**
 * Wire drag-to-dismiss, the pull-to-close handoff, and a body scroll lock onto a sheet.
 *
 * Takes the caller's existing ref rather than creating one: every sheet already holds a
 * ref for its focus trap, and a second ref on the same element would mean keeping two in
 * sync for no gain.
 *
 * @param sheetRef The sheet element — the thing that slides.
 * @param onClose Called when a gesture crosses the dismiss threshold.
 * @param options Per-sheet gesture allowances.
 */
export function useBottomSheet(
  sheetRef: RefObject<HTMLDivElement>,
  onClose: () => void,
  options: BottomSheetOptions = {},
): BottomSheetHandle {
  const { allowStaticContentPull = false } = options;
  // Live gesture state. A ref, not state: it changes on every pointermove and must not
  // re-render the sheet mid-drag.
  const drag = useRef<{
    pointerId: number;
    startY: number;
    lastY: number;
    lastT: number;
    velocity: number;
  } | null>(null);
  // A pull that STARTED at the top of the list and may yet become a dismiss.
  const handoff = useRef<{ pointerId: number; y: number; atTop: boolean } | null>(null);
  const touchHandoff = useRef<{
    startY: number;
    lastY: number;
    lastT: number;
    velocity: number;
    dragging: boolean;
  } | null>(null);
  const closeTimer = useRef<number | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useBodyScrollLock();

  /** Begin tracking a drag from `clientY`. Shared by both gestures. */
  const beginDrag = useCallback(
    (pointerId: number, startY: number, currentY = startY) => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      drag.current = {
        pointerId,
        startY,
        lastY: currentY,
        lastT: performance.now(),
        velocity: 0,
      };
      // Track the finger, so the release transition must not also be running.
      sheet.style.transition = 'none';
      // Set once and never removed. Suppressing the entry animation only WHILE dragging
      // would restore `animation: sheet-in` the moment the finger lifts, and the browser
      // would start a fresh instance — re-playing the whole slide from off-screen
      // instead of snapping back the few pixels the user actually pulled.
      sheet.dataset.entered = 'true';
      const offset = Math.max(0, currentY - startY);
      sheet.style.transform = `translate3d(0, ${offset * DRAG_ELASTIC}px, 0)`;
    },
    [sheetRef],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      // Ignore secondary buttons so a right-click cannot start a drag.
      // Primary button, primary pointer: a second finger must not hijack a drag in
      // flight, and a right-click must not start one.
      if (event.button !== 0 || !event.isPrimary) return;
      // Capture so the gesture survives the pointer leaving the handle — without this a
      // fast flick can lose its own pointerup and strand the sheet mid-drag.
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      beginDrag(event.pointerId, event.clientY);
    },
    [beginDrag],
  );

  /**
   * Record a candidate pull, but only when the region is genuinely scrollable AND
   * already at its top.
   *
   * The scrollable test matters. p2p's consumer is a long currency list that always
   * overflows, so "at the top" is a real, transient state there. Several wallet sheets
   * are short enough not to overflow at all, where scrollTop is permanently 0 — without
   * this guard every downward drag anywhere in the body would start a dismiss, so the
   * whole content area would behave like a drag handle and ordinary presses on rows and
   * buttons would pull the sheet.
   */
  const onScrollPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0 || !event.isPrimary) return;
      const el = event.currentTarget;
      const scrollable = el.scrollHeight > el.clientHeight;
      handoff.current = {
        pointerId: event.pointerId,
        y: event.clientY,
        atTop: (scrollable || allowStaticContentPull) && el.scrollTop <= 0,
      };
    },
    [allowStaticContentPull],
  );

  /** Promote a candidate pull into a real drag once it clears the slop. */
  const onScrollPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch') return;
      const candidate = handoff.current;
      if (!candidate || !candidate.atTop) return;
      if (event.pointerId !== candidate.pointerId) return;
      // The user scrolled into the list before pulling back: they are reading, not
      // dismissing. Drop the candidate so native scrolling owns the rest of the gesture.
      if (event.currentTarget.scrollTop > 0) {
        handoff.current = null;
        return;
      }
      if (event.clientY - candidate.y > HANDOFF_SLOP_PX) {
        handoff.current = null;
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
        // Preserve the original touch origin and paint this first promoted frame
        // immediately, avoiding a dead zone after the handoff threshold.
        beginDrag(candidate.pointerId, candidate.y, event.clientY);
      }
    },
    [beginDrag],
  );

  const clearHandoff = useCallback(() => {
    handoff.current = null;
  }, []);

  const onScroll = useCallback((event: ReactUIEvent<HTMLElement>) => {
    event.currentTarget.dataset.atTop = String(event.currentTarget.scrollTop <= 0);
  }, []);

  const settle = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    sheet.style.transition =
      `transform ${SETTLE_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
    sheet.style.transform = 'translate3d(0, 0, 0)';
  }, [sheetRef]);

  const finishDrag = useCallback(
    (offset: number, velocity: number) => {
      if (offset <= DISMISS_DISTANCE_PX && velocity <= DISMISS_VELOCITY_PX_S) {
        settle();
        return;
      }
      const sheet = sheetRef.current;
      if (!sheet || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        onCloseRef.current();
        return;
      }
      sheet.dataset.closing = 'true';
      sheet.style.transition =
        `transform ${EXIT_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
      sheet.style.transform = 'translate3d(0, 100%, 0)';
      closeTimer.current = window.setTimeout(() => {
        closeTimer.current = null;
        onCloseRef.current();
      }, EXIT_DURATION_MS);
    },
    [settle, sheetRef],
  );

  const onTouchStart = useCallback(
    (event: ReactTouchEvent<HTMLElement>) => {
      if (event.touches.length !== 1) return;
      const el = event.currentTarget;
      const scrollable = el.scrollHeight > el.clientHeight;
      if ((!scrollable && !allowStaticContentPull) || el.scrollTop > 0) return;
      const y = event.touches[0].clientY;
      touchHandoff.current = {
        startY: y,
        lastY: y,
        lastT: performance.now(),
        velocity: 0,
        dragging: false,
      };
    },
    [allowStaticContentPull],
  );

  const onTouchMove = useCallback(
    (event: ReactTouchEvent<HTMLElement>) => {
      const state = touchHandoff.current;
      if (!state || event.touches.length !== 1) return;
      const el = event.currentTarget;
      const y = event.touches[0].clientY;
      const offset = y - state.startY;
      if (!state.dragging && (el.scrollTop > 0 || offset < -HANDOFF_SLOP_PX)) {
        touchHandoff.current = null;
        return;
      }
      if (!state.dragging && offset > HANDOFF_SLOP_PX) {
        const sheet = sheetRef.current;
        if (!sheet) return;
        state.dragging = true;
        sheet.dataset.entered = 'true';
        sheet.style.transition = 'none';
      }
      if (!state.dragging) return;
      event.preventDefault();
      const now = performance.now();
      const dt = now - state.lastT;
      if (dt > 0) state.velocity = ((y - state.lastY) / dt) * 1000;
      state.lastY = y;
      state.lastT = now;
      const sheet = sheetRef.current;
      if (sheet) {
        sheet.style.transform = `translate3d(0, ${Math.max(0, offset) * DRAG_ELASTIC}px, 0)`;
      }
    },
    [sheetRef],
  );

  const onTouchEnd = useCallback(
    (event: ReactTouchEvent<HTMLElement>) => {
      const state = touchHandoff.current;
      touchHandoff.current = null;
      if (!state?.dragging) return;
      const y = event.changedTouches[0]?.clientY ?? state.lastY;
      finishDrag(Math.max(0, y - state.startY), state.velocity);
    },
    [finishDrag],
  );

  const onTouchCancel = useCallback(() => {
    const state = touchHandoff.current;
    touchHandoff.current = null;
    if (state?.dragging) settle();
  }, [settle]);

  useEffect(() => {
    function handleMove(event: PointerEvent): void {
      const state = drag.current;
      const sheet = sheetRef.current;
      if (!state || !sheet) return;
      // Only the finger that started the drag may move it. Without this a second touch
      // anywhere on screen feeds its coordinates into this gesture.
      if (event.pointerId !== state.pointerId) return;

      const now = performance.now();
      const dt = now - state.lastT;
      if (dt > 0) {
        // Instantaneous velocity, sampled per move rather than averaged over the whole
        // gesture, so a slow drag that ends in a flick still reads as a flick.
        state.velocity = ((event.clientY - state.lastY) / dt) * 1000;
        state.lastY = event.clientY;
        state.lastT = now;
      }
      // Downward only: Math.max pins the top so an upward pull cannot lift the sheet
      // off its stop and reveal the page behind it. The sheet travels a FRACTION of the
      // pull (p2p's dragElastic) so it resists rather than tracking 1:1 — the raw offset
      // is what the dismiss test uses.
      const offset = Math.max(0, event.clientY - state.startY);
      sheet.style.transform = `translate3d(0, ${offset * DRAG_ELASTIC}px, 0)`;
    }

    function handleUp(event: PointerEvent): void {
      const state = drag.current;
      if (!state || event.pointerId !== state.pointerId) return;
      drag.current = null;

      // Raw pointer offset, not the elastic-damped travel — p2p tests info.offset.y,
      // which is the unmodified delta.
      const offset = Math.max(0, event.clientY - state.startY);
      finishDrag(offset, state.velocity);
    }

    /**
     * A cancelled gesture is not a release. The OS withdrawing a pointer — a system
     * gesture, a call arriving — must never be read as intent to dismiss, so this only
     * unwinds the drag. Routing cancel through handleUp would let an interrupted flick
     * close a sheet the user never let go of.
     */
    function handleCancel(event: PointerEvent): void {
      const state = drag.current;
      if (!state || event.pointerId !== state.pointerId) return;
      drag.current = null;
      handoff.current = null;
      settle();
    }

    // On the window so a gesture that leaves the sheet still resolves; a release outside
    // the element would otherwise strand the sheet mid-drag.
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
    return () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
    };
  }, [finishDrag, settle, sheetRef]);

  return {
    dragProps: { onPointerDown },
    scrollProps: {
      onPointerDown: onScrollPointerDown,
      onPointerMove: onScrollPointerMove,
      onPointerUp: clearHandoff,
      onPointerCancel: clearHandoff,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
      onScroll,
    },
  };
}
